/**
 * @render react
 * @name PostMeta
 */

import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { withPolaris } from 'Components/protons/Polaris';

import { Date } from 'Components/atoms/Date';
import { Image } from 'Components/atoms/Image';
import { Link } from 'Components/molecules/Link';

import './_style.scss';

/**
 * PostMeta component
 *
 * @param {Object} props - the props object
 * @param {string} props.author - Author name
 * @param {string} props.authorPrefix - Author name prefix string
 * @param {string} props.authorUrl  - URL to the author page
 * @param {string} props.authorImage - Object containing Url, width, height and alt text
 * @param {string} props.authors - Array of authors containing the following keys: author, authorUrl
 * @param {string} props.date - Date to be displayed
 * @param {string} props.extra - Extra component to be displayed within the post meta container. Possible uses cases: display comment count to the right
 * @param {boolean} loadStyles - Whether to apply the class names for styling
 * @param {Object} props.extraClassNames - Extra classes to be added to the component
 * @param {Object} props.context - Extra information coming from Polaris provider
 * @param {Object} props.showDate - Whether to show date or not
 * @param {Object} props.showTime - Whether to show time or not
 */

/**
 * Our main component
 * @param {*}
 */
const PostMeta = (
  {
    author,
    authorUrl,
    authorImage,
    authors,
    date,
    authorPrefix,
    extraComponent,
    context: { cssPrefix },
    loadStyles,
    extraClassNames,
    time,
    showDate,
    showTime,
  },
) => {
  const renderSingleAuthor = (name, url) => (
    url ? (
      <Link href={url}>
        {name}
      </Link>
    ) : (<span>{name}</span>)
  );

  return (
    <div className={classNames({
      [`${cssPrefix}__post-meta`]: loadStyles,
      ...extraClassNames,
    })}
    >
      <div className={classNames({
        [`${cssPrefix}__post-meta--meta`]: loadStyles,
      })}
      >
        {authorImage && authorImage.src && (
          <div className={`${cssPrefix}__post-meta--author-image`}>
            <Image {...authorImage} size="author" />
          </div>
        )}

        {(author || authors) && (
          <div className={`${cssPrefix}__post-meta--author`}>
            {authorPrefix && <span className={`${cssPrefix}__post-meta--author-prefix`}>{authorPrefix}</span>}

            <span className={`${cssPrefix}__post-meta--author-name`}>
              {author && (renderSingleAuthor(author, authorUrl))}

              {author && authors && (', ')}

              {authors && authors.map((singleAuthor, index) => (
                <Fragment key={`post-meta__author--${index}`}>
                  {(index ? ', ' : '')}
                  {renderSingleAuthor(singleAuthor.author, singleAuthor.authorUrl)}
                </Fragment>
              ))}
            </span>
          </div>
        )}

        <div className={`${cssPrefix}__post-meta--separator`} />

        {(date || (showTime && time)) && (
          <>
            <div className={`${cssPrefix}__post-meta--date`}>
              {(showDate && date) && <Date date={date} />}
              {(showTime && time) && <span className={`${cssPrefix}__time`}>{time}</span>}
            </div>
            <noscript>
              <style>{`.${cssPrefix}__post-meta--date { display: none; } .${cssPrefix}__post-meta--date.no-script { display: block; padding-left: 45px } `}</style>
              <div className={`${cssPrefix}__post-meta--date no-script`}>
                {date && <Date date={date} />}
              </div>
            </noscript>
          </>
        )}
      </div>

      {extraComponent && (
        <div className={classNames({
          [`${cssPrefix}__post-meta--extra`]: loadStyles,
        })}
        >
          {extraComponent}
        </div>
      )}
    </div>
  );
};

/**
 * Valid props
 */
PostMeta.propTypes = {
  author: PropTypes.string,
  authorUrl: PropTypes.string,
  authorImage: PropTypes.oneOfType([
    PropTypes.shape({
      src: PropTypes.string,
      alt: PropTypes.string,
      height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
    PropTypes.bool,
  ]),
  authors: PropTypes.arrayOf(
    PropTypes.shape({
      author: PropTypes.string,
      authorUrl: PropTypes.string,
    }),
  ),
  date: PropTypes.string,
  authorPrefix: PropTypes.string,
  extraComponent: PropTypes.node,
  context: PropTypes.shape(),
  loadStyles: PropTypes.bool,
  extraClassNames: PropTypes.shape(),
  time: PropTypes.string,
  showDate: PropTypes.bool,
  showTime: PropTypes.bool,
};

/**
 * Default props
 */
PostMeta.defaultProps = {
  author: '',
  authorUrl: '',
  authorImage: {},
  authors: null,
  date: '',
  authorPrefix: 'by: ',
  extraComponent: null,
  context: {},
  loadStyles: true,
  extraClassNames: {},
  time: '',
  showDate: true,
  showTime: false,
};

export default withPolaris(PostMeta);
